import { defineRequest, defineRequestWithParam } from "../axios"
import type {
    AppPushCopyResponse,
    AppPushCreateResponse,
    AppPushDetailResponse,
    AppPushListResponse,
    AppPushReceiversResponse,
    AppPushReviewersResponse,
    AppPushReviewResponse,
    AppPushStatusCountResponse,
    AppPushTestResponse,
    AppPushUpdateResponse,
    AssetStatusListResponse,
    CreateEdmPayload,
    CreateSmsPayload,
    EditSmsPayload,
    EdmCopyResponse,
    EdmCreateResponse,
    EdmDetailResponse,
    EdmListResponse,
    EdmPreferencesResponse,
    EdmReviewersResponse,
    EdmReviewResponse,
    EdmSendPreviewResponse,
    EdmStatusCountResponse,
    EdmUpdateResponse,
    MemberMessageParamsResponse,
    ReviewEdmPayload,
    ReviewSmsPayload,
    SendEdmPreviewPayload,
    SendSmsPreviewPayload,
    ShortUrlServicesResponse,
    SmsCopyResponse,
    SmsCreateResponse,
    SmsDetailResponse,
    SmsListResponse,
    SmsReviewersResponse,
    SmsReviewResponse,
    SmsSendPreviewResponse,
    SmsStatusCountResponse,
    SmsUpdateResponse,
    UpdateEdmPayload,
    UpdateEdmPreferencesPayload,
} from "./types"

//============================ sms ============================//

// 取得 sms 素材各種狀態的數量
export const getSmsStatusCount = defineRequest<SmsStatusCountResponse>({
    url: "/assets/sms/counts/",
    method: "get",
})

// 取得 sms
export const getSmsList = defineRequest<SmsListResponse>({
    url: "/assets/sms/",
    method: "get",
})

// 取得單個 sms
export const getSms = defineRequestWithParam<SmsDetailResponse>({
    url: (smsId: string) => `/assets/sms/${smsId}/`,
    method: "get",
})

// 新增 sms
export const createSms = defineRequest<SmsCreateResponse, CreateSmsPayload>({
    url: "/assets/sms/",
    method: "post",
})

// 編輯 sms
export const updateSms = defineRequestWithParam<SmsUpdateResponse, EditSmsPayload>({
    url: (smsId: string) => `/assets/sms/${smsId}/`,
    method: "put",
})

// 複製 sms
export const copySms = defineRequestWithParam<SmsCopyResponse>({
    url: (smsId: string) => `/assets/sms/${smsId}/copy/`,
    method: "post",
})

// 停用 sms
export const disableSms = defineRequestWithParam<void>({
    url: (smsId: string) => `/assets/sms/${smsId}/disable/`,
    method: "put",
})

//  刪除 sms
export const deleteSms = defineRequestWithParam<void>({
    url: (smsId: string) => `/assets/sms/${smsId}/`,
    method: "delete",
})

// 送審 sms
export const submitSms = defineRequestWithParam<void>({
    url: (smsId: string) => `/assets/sms/${smsId}/submit/`,
    method: "post",
})

// 審核 sms
export const reviewSms = defineRequestWithParam<SmsReviewResponse, ReviewSmsPayload>({
    url: (smsId: string) => `/assets/sms/${smsId}/review/`,
    method: "post",
})

// 取得 sms 審核紀錄
export const getSmsReviewers = defineRequestWithParam<SmsReviewersResponse>({
    url: (uuid: string) => `/assets/sms/${uuid}/reviewers/`,
    method: "get",
})

// 預覽 sms
export const sendSmsPreview = defineRequest<SmsSendPreviewResponse, SendSmsPreviewPayload>({
    url: "/assets/sms/send-preview/",
    method: "post",
})

// 取得 sms 短網址服務商
export const getShortUrlServices = defineRequest<ShortUrlServicesResponse>({
    url: "/assets/sms/short-url/services/",
    method: "get",
})

//============================ APP推播 ============================//

// 取得 app push 素材各狀態數量
export const getAppPushStatusCount = defineRequest<AppPushStatusCountResponse>({
    url: "/assets/push/counts/",
    method: "get",
})

// 取得 APP推播
export const getAppPushes = defineRequest<AppPushListResponse>({
    url: "/assets/push/",
    method: "get",
})

// 取得單個 APP推播
export const getAppPush = defineRequestWithParam<AppPushDetailResponse>({
    url: (appPushId: string) => `/assets/push/${appPushId}/`,
    method: "get",
})

// 新增 APP推播
export const createAppPush = defineRequest<AppPushCreateResponse>({
    url: "/assets/push/",
    method: "post",
})

// 編輯 APP推播
export const updateAppPush = defineRequestWithParam<AppPushUpdateResponse>({
    url: (appPushId: string) => `/assets/push/${appPushId}/`,
    method: "put",
})

// 複製 APP推播
export const copyAppPush = defineRequestWithParam<AppPushCopyResponse>({
    url: (appPushId: string) => `/assets/push/${appPushId}/copy/`,
    method: "post",
})

// 停用 APP推播
export const disableAppPush = defineRequestWithParam<void>({
    url: (appPushId: string) => `/assets/push/${appPushId}/disable/`,
    method: "put",
})

// 刪除 APP推播
export const deleteAppPush = defineRequestWithParam<void>({
    url: (appPushId: string) => `/assets/push/${appPushId}/`,
    method: "delete",
})

// 送審 APP推播
export const submitAppPush = defineRequestWithParam<void>({
    url: (appPushId: string) => `/assets/push/${appPushId}/submit/`,
    method: "post",
})

// 審核 APP推播
export const reviewAppPush = defineRequestWithParam<AppPushReviewResponse>({
    url: (appPushId: string) => `/assets/push/${appPushId}/review/`,
    method: "post",
})

// 取得 APP推播 審核紀錄
export const getAppPushReviewers = defineRequestWithParam<AppPushReviewersResponse>({
    url: (uuid: string) => `/assets/push/${uuid}/reviewers/`,
    method: "get",
})

// 取得 APP推播 推播人員列表
export const getAppPushReceivers = defineRequest<AppPushReceiversResponse>({
    url: "/assets/push/receivers/",
    method: "get",
})

// APP推播 測試推播
export const testAppPush = defineRequest<AppPushTestResponse>({
    url: "/assets/push/test/",
    method: "post",
})

//============================ EDM ============================//

// 取得 edm 偏好設定
export const getEdmPreferences = defineRequest<EdmPreferencesResponse>({
    url: "/assets/edm/preferences/",
    method: "get",
})

// 更新 edm 偏好設定
export const updateEdmPreferences = defineRequest<EdmPreferencesResponse, UpdateEdmPreferencesPayload>({
    url: "/assets/edm/preferences/",
    method: "put",
})

// 取得 edm 素材各狀態數量
export const getEdmStatusCount = defineRequest<EdmStatusCountResponse>({
    url: "/assets/edm/counts/",
    method: "get",
})

// 取得 edm 素材列表
export const getEdmList = defineRequest<EdmListResponse>({
    url: "/assets/edm/",
    method: "get",
})

// 取得單個 edm 素材
export const getEdm = defineRequestWithParam<EdmDetailResponse>({
    url: (edmAssetId: string) => `/assets/edm/${edmAssetId}/`,
    method: "get",
})

// 新增 edm 素材
export const createEdm = defineRequest<EdmCreateResponse, CreateEdmPayload>({
    url: "/assets/edm/",
    method: "post",
})

// 編輯 edm 素材
export const updateEdm = defineRequestWithParam<EdmUpdateResponse, UpdateEdmPayload>({
    url: (edmAssetId: string) => `/assets/edm/${edmAssetId}/`,
    method: "put",
})

// 複製 edm 素材
export const copyEdm = defineRequestWithParam<EdmCopyResponse>({
    url: (edmAssetId: string) => `/assets/edm/${edmAssetId}/copy/`,
    method: "post",
})

// 停用 edm 素材
export const disableEdm = defineRequestWithParam<void>({
    url: (edmAssetId: string) => `/assets/edm/${edmAssetId}/disable/`,
    method: "put",
})

// 刪除 edm 素材
export const deleteEdm = defineRequestWithParam<void>({
    url: (edmAssetId: string) => `/assets/edm/${edmAssetId}/`,
    method: "delete",
})

// 送審 edm 素材
export const submitEdm = defineRequestWithParam<void>({
    url: (edmAssetId: string) => `/assets/edm/${edmAssetId}/submit/`,
    method: "post",
})

// 審核 edm 素材
export const reviewEdm = defineRequestWithParam<EdmReviewResponse, ReviewEdmPayload>({
    url: (edmAssetId: string) => `/assets/edm/${edmAssetId}/review/`,
    method: "post",
})

// 取得 edm 素材 審核紀錄
export const getEdmReviewers = defineRequestWithParam<EdmReviewersResponse>({
    url: (uuid: string) => `/assets/edm/${uuid}/reviewers/`,
    method: "get",
})

// 取得 預覽收件人列表
export const getEdmReceivers = defineRequest<void>({
    url: "/assets/edm/receivers/",
    method: "get",
})

// 新增 預覽收件人
export const addEdmReceiver = defineRequest<void>({
    url: "/assets/edm/receivers/",
    method: "post",
})

// 刪除 預覽收件人
export const deleteEdmReceiver = defineRequestWithParam<void>({
    url: (uuid: string) => "/assets/edm/receivers/" + uuid + "/",
    method: "delete",
})

// 預覽 EDM
export const sendEdmPreview = defineRequest<EdmSendPreviewResponse, SendEdmPreviewPayload>({
    url: "/assets/edm/send-preview/",
    method: "post",
})

//============================ 素材管理 ============================//

// 取得素材狀態
export const getAssetStatus = defineRequest<AssetStatusListResponse>({
    url: "/assets/status/",
    method: "get",
})

// 取得個人化變數
export const getMemberMessageParams = defineRequest<MemberMessageParamsResponse>({
    url: "/assets/member-message-params/",
    method: "get",
})
